import { FC, PropsWithChildren, useState } from "react";

import { Box, ButtonBase, Collapse, Stack } from "@mui/material";

import { DashboardButton } from "@/dashboard-design-system/DashboardButton";
import { DashboardInformationTooltip } from "@/dashboard-design-system/DashboardTooltip/DashboardInformationTooltip";
import { DashboardIcon } from "@/dashboard-design-system/Icon/DashboardIcon";
import { DashboardTypography } from "@/dashboard-design-system/Typography";
import { Add, ChevronDown } from "@/icons/outlined";

interface CollapsibleQuestionSectionProps {
  title: string;
  addButtonLabel: string;
  onAddQuestion: () => void;
  addButtonTestId?: string;
  titleTooltip?: string;
  defaultExpanded?: boolean;
  sectionTestId?: string;
}

export const CollapsibleQuestionSection: FC<PropsWithChildren<CollapsibleQuestionSectionProps>> = ({
  title,
  addButtonLabel,
  onAddQuestion,
  addButtonTestId,
  titleTooltip,
  defaultExpanded = true,
  sectionTestId,
  children,
}) => {
  const [isExpanded, setIsExpanded] = useState(defaultExpanded);

  return (
    <Stack
      data-test={sectionTestId}
      sx={(theme) => ({
        outline: `1px solid ${theme.palette.border.neutral.quiet}`,
        outlineOffset: -1,
        borderRadius: 1,
      })}
    >
      <ButtonBase
        disableRipple
        onClick={() => setIsExpanded((previous) => !previous)}
        aria-expanded={isExpanded}
        data-test={sectionTestId ? `${sectionTestId}-toggle` : undefined}
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          width: "100%",
          px: 3,
          py: 2.5,
          pr: 4,
          borderRadius: "inherit",
          ...(!isExpanded && {
            "&:hover": { backgroundColor: "surface.neutral.supershy" },
          }),
        }}
      >
        <Stack direction="row" alignItems="center" spacing={1}>
          <DashboardTypography variant="subtitle2" sx={{ color: "text.neutral.quiet", textTransform: "uppercase" }}>
            {title}
          </DashboardTypography>
          {titleTooltip && (
            <Box component="span" sx={{ display: "inline-flex" }} onClick={(event) => event.stopPropagation()}>
              <DashboardInformationTooltip
                title={titleTooltip}
                placement="top"
                slotProps={{ icon: { size: "small", vibe: "text-neutral-quiet" } }}
              />
            </Box>
          )}
        </Stack>
        <DashboardIcon
          component="span"
          size="medium"
          vibe="text-neutral-quiet"
          sx={(theme) => ({
            display: "inline-flex",
            transform: isExpanded ? "rotate(180deg)" : "none",
            transition: theme.transitions.create("transform", { duration: theme.transitions.duration.shorter }),
          })}
        >
          <ChevronDown />
        </DashboardIcon>
      </ButtonBase>

      <Collapse in={isExpanded}>
        <Stack spacing={1} sx={{ px: 3 }}>
          {children}
        </Stack>
        <Box sx={{ mt: 2, px: 3, pb: 2.5 }}>
          <DashboardButton
            variant="outlined"
            vibe="brand"
            startIcon={<Add />}
            onClick={onAddQuestion}
            data-test={addButtonTestId}
          >
            {addButtonLabel}
          </DashboardButton>
        </Box>
      </Collapse>
    </Stack>
  );
};
